"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { fetchPostByCategories } from "@/services/postServices"
import { getSiteIdFromHost } from "@/sites/config"
import { PostData } from "@/types/types"

interface UseFetchPostsByCategoryOptions {
  categoryIds?: string[]
  page?: number
  pageSize?: number
  enabled?: boolean
  locale?: string
  siteId?: string
  domain: string
}

interface UseFetchPostsByCategoryResult {
  posts: PostData[]
  loading: boolean
  error: string | null
  totalPages: number
  total: number
  refetch: () => Promise<void>
}

export const useFetchPostsByCategory = ({
  categoryIds = [],
  page = 1,
  pageSize = 10,
  enabled = true,
  locale = "vi",
  siteId: siteIdProp,
  domain,
}: UseFetchPostsByCategoryOptions): UseFetchPostsByCategoryResult => {
  const [posts, setPosts] = useState<PostData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const siteId = useMemo(() => {
      if (siteIdProp) return siteIdProp // ✅ ưu tiên prop
      if (typeof window === "undefined") return ""
      const host = window.parent?.location?.host || window.location.host
      return getSiteIdFromHost(host)
    }, [siteIdProp])

  const categoryIdsKey = useMemo(() => {
    if (!categoryIds || categoryIds.length === 0) return `all__${locale}`
    return `${categoryIds.sort().join(",")}__${locale}`
  }, [categoryIds, locale])

  const fetchPosts = useCallback(async () => {
    if (!enabled) {
      setPosts([])
      setTotalPages(1)
      setTotal(0)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Luôn dùng public client: `fetchPostByCategories` với mảng rỗng = mọi category.
      // Service admin `fetchPosts` đi qua HttpClient (gắn Bearer auth_token), nên khi
      // editor đang đăng nhập nó trả cả bài draft / bài chưa gán website — khác hẳn
      // những gì khách ẩn danh nhìn thấy.
      const response = await fetchPostByCategories(categoryIds, {
        page: page.toString(),
        pageSize: String(pageSize),
        locale,
        siteId,
        domain: domain || undefined,
      })

      if (response?.data) {
        setPosts(response.data)

        if (response.meta?.pagination) {
          const totalItems = response.meta.pagination.total || 0
          const pageSizeNum = response.meta.pagination.pageSize || pageSize
          setTotal(totalItems)
          setTotalPages(Math.ceil(totalItems / pageSizeNum))
        } else {
          setTotal(response.data.length)
          setTotalPages(1)
        }
      } else {
        setPosts([])
        setTotal(0)
        setTotalPages(1)
      }
    } catch (err) {
      console.error("Lỗi khi lấy các bài viết bằng category:", err)
      setError(err instanceof Error ? err.message : "Lỗi không xác định.")
      setPosts([])
      setTotal(0)
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }, [categoryIdsKey, page, pageSize, enabled, siteId, locale, domain])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  return {
    posts,
    loading,
    error,
    totalPages,
    total,
    refetch: fetchPosts,
  }
}
